// Mapa funcional de MADECI ERP. La FORMA es la de Random (familiar para quien lo usa hace años): un
// menú horizontal arriba con Stock, Compras, Ventas, Pago de Clientes, Pago a Proveedores,
// Operaciones, DTE, Informes y Configuración; cada uno despliega sus funciones con nombres conocidos
// (Punto de Venta, Nota de Venta, Cobrar factura, Pagar factura…). De este archivo salen el menú,
// las rutas (#/ventas/notas), los accesos rápidos, los comandos de Ctrl+K y la matriz A/B/C que se ve
// en VALIDACIÓN/DEV. Una función aparece solo si el usuario tiene su permiso.
//
// MENÚ ≠ VENTANA. Abrir un menú solo muestra opciones. Elegir una CONSULTA (Notas de venta, Consulta
// de stock…) la muestra en el área central sin crear pestañas. Elegir una OPERACIÓN (`accion`: Punto
// de Venta, Nota de Venta, Nueva OD) abre una pestaña de trabajo, igual que abrir un objeto (NV 10542,
// un cliente, un producto).
//
// Estado del BACKEND de cada función:
//   A · operativo  — hay contrato real y la pantalla lo usa.
//   B · parcial    — hay contrato para una parte; el resto está pendiente.
//   C · pendiente  — la pantalla está preparada, pero no hay backend: se ve, no opera.
// Una función sin `abre` todavía no está construida: muestra la pantalla preparada, que dice qué API
// la alimentará y qué acciones quedan pendientes. Nunca simula éxito ni escribe datos.

export type EstadoBackend = 'A' | 'B' | 'C';

/** Consulta ya construida (componente existente) que se muestra en el área central. */
export type TipoVista =
  | 'notas'
  | 'documentos-emitidos'
  | 'clientes'
  | 'productos'
  | 'solicitudes'
  | 'mi-turno'
  | 'turnos-caja'
  | 'operadores-caja'
  | 'excepciones-caja'
  | 'mapa'
  | 'stock-bodega'
  | 'movimientos'
  | 'kardex'
  | 'ajustes-stock'
  | 'inventario-fisico'
  | 'valorizacion'
  | 'ordenes-despacho'
  | 'maestro-logistica'
  | 'cartola'
  | 'cxc'
  | 'cartola-vendedor'
  | 'vencimientos'
  | 'historial-cobros'
  | 'cheques'
  | 'recepcion-rutas'
  | 'rendicion-odd'
  | 'cobros-ruta'
  | 'caja-tesoreria'
  | 'autorizaciones-pago'
  | 'cuentas-bancarias'
  | 'cartola-proveedor'
  | 'cxp'
  | 'historial-pagos'
  | 'ordenes-compra'
  | 'recepciones'
  | 'facturas-compra'
  | 'recibidos-compra'
  | 'registro-compras'
  | 'notas-compra'
  | 'devoluciones-compra'
  | 'aprobaciones-compras'
  | 'aprobaciones-ventas'
  | 'documentos-guardados'
  | 'documentos-random'
  | 'proveedores'
  | 'costos-compra'
  | 'esperando-nc'
  | 'costos-logisticos'
  | 'historial-compras'
  | 'cotizaciones'
  | 'pedidos'
  | 'correcciones-venta'
  | 'nc-directa'
  | 'consignaciones'
  | 'despachos-venta'
  | 'transferencias'
  | 'reservas'
  | 'impresion-masiva'
  | 'vales'
  | 'informes'
  | 'panel-negocio'
  | 'dte'
  | 'config'
  | 'tratamiento-precios'
  | 'incidencias-od';

/** Operación que abre una pestaña de trabajo. */
export type TipoOperacion = 'nota' | 'od' | 'cobro' | 'pago' | 'compra' | 'cotizacion';

export interface Pantalla {
  /** Ruta estable: `#/ventas/notas`. */
  ruta: string;
  nombre: string;
  /** Sin el permiso, la función no aparece. */
  permiso?: string;
  backend: EstadoBackend;
  descripcion: string;
  /** Contratos que la alimentan (o la alimentarán). */
  api?: string[];
  habilitadas?: string[];
  pendientes?: string[];
  /** Ya construida: qué consulta muestra o qué operación abre. */
  abre?: { tipo: TipoVista | TipoOperacion; params?: Record<string, string> };
  /** Checkpoint en que se construye (si todavía no está). */
  checkpoint?: 'UI-2' | 'UI-3' | 'UI-4' | 'UI-5';
  /** La fuente operacional no existe todavía (no se inventa CxC/CxP). */
  sinFuente?: boolean;
  tecla?: string;
  /** OPERACIÓN: abre una pestaña de trabajo (Punto de Venta, Nota de Venta, Nueva OD). */
  accion?: boolean;
  /** Solo por Ctrl+K (no ocupa lugar en el menú). */
  oculta?: boolean;
  /** Otras palabras con que Ctrl+K la encuentra. */
  alias?: string[];
  /** Sigla del documento, como en Random (NVV, GDV, FCV…). */
  codigo?: string;
}

export interface Seccion {
  /** Menú de texto (arriba) en que aparece el grupo, dentro de su subsistema: «Documentos». */
  menu?: string;
  /** Título del grupo dentro del desplegable: «Operación de ventas». */
  nombre?: string;
  pantallas: Pantalla[];
}

export interface Modulo {
  id: string;
  /** Nombre familiar (como en Random): «Pago de Clientes». */
  nombre: string;
  /** Nombre corto opcional para comparar: «Cobros». */
  corto: string;
  sigla: string;
  secciones: Seccion[];
}

const ui5 = 'UI-5' as const;

/** Función preparada (sin backend o sin pantalla), en pocas líneas. */
const prep = (
  ruta: string,
  nombre: string,
  permiso: string,
  backend: EstadoBackend,
  descripcion: string,
  pendientes: string[],
  checkpoint: NonNullable<Pantalla['checkpoint']>,
  extra: Partial<Pantalla> = {},
): Pantalla => ({ ruta, nombre, permiso, backend, descripcion, pendientes, checkpoint, ...extra });

const BASE: Modulo[] = [
  // ------------------------------------------------------------------ STOCK
  {
    id: 'stock',
    nombre: 'Stock',
    corto: 'Stock',
    sigla: 'ST',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'stock/consulta',
            nombre: 'Consulta de stock',
            permiso: 'inventario.ver',
            backend: 'A',
            alias: ['abrir stock', 'existencias', 'disponible', 'producto'],
            descripcion:
              'Físico, reservado y disponible por producto; Enter abre la ficha del producto. Derivado del libro: no se edita.',
            api: ['/productos', '/inventario/disponibilidad'],
            abre: { tipo: 'productos', params: { vista: 'stock' } },
          },
          {
            ruta: 'stock/por-bodega',
            nombre: 'Stock por bodega',
            permiso: 'inventario.ver',
            backend: 'A',
            alias: ['saldos', 'existencias por bodega', 'camion'],
            descripcion:
              'Físico, reservado y disponible de cada producto en una bodega o camión; Enter muestra su kardex.',
            api: ['/inventario/disponibilidad', '/productos/resumen'],
            abre: { tipo: 'stock-bodega' },
          },
          {
            ruta: 'stock/movimientos',
            nombre: 'Movimientos',
            permiso: 'inventario.ver',
            backend: 'A',
            descripcion: 'Movimientos del libro de inventario por bodega, tipo y fecha.',
            api: ['/inventario/movimientos'],
            abre: { tipo: 'movimientos' },
          },
          {
            ruta: 'stock/kardex',
            nombre: 'Kardex',
            permiso: 'inventario.ver',
            backend: 'A',
            descripcion: 'Kardex de un producto en una bodega, con saldo corrido.',
            api: ['/inventario/kardex'],
            abre: { tipo: 'kardex' },
          },
          {
            ruta: 'stock/transferencias',
            nombre: 'Transferencias',
            permiso: 'inventario.traslado.crear',
            backend: 'A',
            descripcion:
              'Traslados entre ubicaciones (bodega → camión, entre bodegas): borrador y confirmación, que mueve el libro.',
            api: ['/inventario/traslados', '/inventario/traslados/:id/confirmar'],
            alias: ['traslado', 'gti'],
            abre: { tipo: 'transferencias' },
          },
          {
            ruta: 'stock/ajustes',
            nombre: 'Ajustes',
            permiso: 'inventario.ver',
            backend: 'A',
            alias: ['merma', 'rotura', 'sobrante', 'ajuste de stock'],
            descripcion:
              'Guía de ajuste numerada con varios productos (merma, rotura, vencimiento, sobrante…), con motivo obligatorio: movimientos en el libro, nunca una edición del saldo.',
            api: ['/inventario/guias-ajuste', '/inventario/movimientos'],
            abre: { tipo: 'ajustes-stock' },
          },
          {
            ruta: 'stock/fisico',
            nombre: 'Inventario físico',
            permiso: 'inventario.ver',
            backend: 'A',
            alias: ['toma de inventario', 'conteo'],
            descripcion:
              'Hoja de inventario por bodega: conteo ciego; al cerrar, cada diferencia es un ajuste en el libro.',
            api: ['/inventario/conteos'],
            abre: { tipo: 'inventario-fisico' },
          },
          {
            ruta: 'stock/reservas',
            nombre: 'Reservas',
            permiso: 'ventas.reserva.ver',
            backend: 'A',
            descripcion:
              'NV confirmadas sin reservar, reservas por vencer y sobrecompromisos (reservado mayor que el físico).',
            api: [
              '/ventas/reservas/pendientes',
              '/inventario/reservas/por-expirar',
              '/inventario/reservas/sobrecompromisos',
            ],
            abre: { tipo: 'reservas' },
          },
          {
            ruta: 'stock/valorizacion',
            nombre: 'PMP / valorización',
            permiso: 'valorizacion.ver',
            backend: 'A',
            alias: ['pmp', 'costo', 'valor del inventario'],
            descripcion:
              'Precio medio ponderado y valor del inventario por producto (solo lectura); lo que no tiene costo aprobado queda pendiente, no se inventa.',
            api: ['/valorizacion/saldos'],
            abre: { tipo: 'valorizacion' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ COMPRAS
  {
    id: 'compras',
    nombre: 'Compras',
    corto: 'Compras',
    sigla: 'CO',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'compras/ordenes',
            nombre: 'Órdenes de Compra',
            permiso: 'compras.oc.ver',
            backend: 'A',
            descripcion:
              'OC con aprobación (otra persona), emisión, recepción, cierre corto y cancelación.',
            api: ['/compras/ordenes', '/compras/ordenes/:id'],
            alias: ['oc', 'orden de compra'],
            abre: { tipo: 'ordenes-compra' },
          },
          {
            ruta: 'compras/oc-nueva',
            nombre: 'Nueva orden de compra',
            permiso: 'compras.oc.crear',
            backend: 'A',
            accion: true,
            descripcion: 'OC nueva: proveedor, bodega de destino, productos y precios.',
            api: ['/compras/ordenes'],
            alias: ['nueva oc', 'crear oc'],
            abre: { tipo: 'compra', params: { doc: 'oc' } },
          },
          {
            ruta: 'compras/recepciones',
            nombre: 'Recepciones',
            permiso: 'compras.recepcion.ver',
            backend: 'A',
            descripcion:
              'Recepción con o sin OC; confirmar es lo que entra al stock; la reversa la aprueba otra persona.',
            api: ['/compras/recepciones', '/compras/recepciones/:id/confirmar'],
            alias: ['recepcion', 'guia de recepcion'],
            abre: { tipo: 'recepciones' },
          },
          {
            ruta: 'compras/recepcion-sin-oc',
            nombre: 'Guía de recepción',
            permiso: 'compras.recepcion.crear',
            backend: 'A',
            accion: true,
            alias: ['grc', 'recepcion sin oc', 'recibir mercaderia', 'nueva recepcion'],
            descripcion:
              'Recibir mercadería con la guía del proveedor: entra al stock al grabar (lo roto no entra).',
            api: ['/compras/recepciones'],
            abre: { tipo: 'compra', params: { doc: 'recepcion' } },
          },
          {
            ruta: 'compras/facturas',
            nombre: 'Facturas de Compra',
            permiso: 'compras.factura.ver',
            backend: 'A',
            descripcion:
              'Facturas del proveedor: identificar líneas, conciliar con lo recibido, aceptar (deja la deuda).',
            api: ['/compras/facturas'],
            alias: ['factura de compra', 'fcc'],
            abre: { tipo: 'facturas-compra' },
          },
          {
            ruta: 'compras/recibidos',
            nombre: 'Facturas recibidas (XML)',
            permiso: 'compras.factura.ver',
            backend: 'A',
            descripcion:
              'Las facturas que los proveedores mandan al correo de intercambio: se registran sin digitar (el XML trae todo).',
            api: ['/compras/recibidos'],
            alias: ['xml', 'dte recibido', 'bandeja de compras', 'correo de intercambio'],
            abre: { tipo: 'recibidos-compra' },
          },
          {
            ruta: 'compras/registro-compras',
            nombre: 'Registro de Compras (SII)',
            permiso: 'compras.factura.ver',
            backend: 'A',
            descripcion:
              'El mes según el SII contra el XML recibido y lo registrado; acuse o reclamo de cada factura (8 días).',
            api: ['/compras/rcv/cuadratura'],
            alias: ['rcv', 'registro de compras', 'acuse de recibo', 'reclamo', 'cuadratura sii'],
            abre: { tipo: 'registro-compras' },
          },
          {
            ruta: 'compras/factura-nueva',
            nombre: 'Registrar factura de compra',
            permiso: 'compras.factura.crear',
            backend: 'A',
            accion: true,
            alias: ['fcc', 'nueva factura de compra', 'digitar factura'],
            descripcion:
              'Digitar la factura del proveedor: entra al stock con ella o se cruza con las guías ya recibidas.',
            api: ['/compras/facturas'],
            abre: { tipo: 'compra', params: { doc: 'factura' } },
          },
          {
            ruta: 'compras/ingreso-sin-factura',
            nombre: 'Ingreso sin factura',
            permiso: 'compras.factura.crear',
            backend: 'A',
            accion: true,
            alias: [
              'isf',
              'ingreso sin factura',
              'compra por tercero',
              'costa delivery',
              'mercaderia sin factura',
            ],
            descripcion:
              'Mercadería que compró un tercero (Costa Delivery) y no se factura a MADECI: entra al stock con su costo neto y bruto y la deuda con el proveedor.',
            api: ['/compras/facturas/directa'],
            abre: { tipo: 'compra', params: { doc: 'ingreso' } },
          },
          {
            ruta: 'compras/notas',
            nombre: 'Notas de Crédito / Débito',
            permiso: 'compras.factura.ver',
            backend: 'A',
            descripcion: 'NC y ND de proveedores: registrar, imputar (qué corrigen) y aceptar.',
            api: ['/compras/notas'],
            alias: ['nota de credito proveedor'],
            abre: { tipo: 'notas-compra' },
          },
          {
            ruta: 'compras/devoluciones',
            nombre: 'Devoluciones',
            permiso: 'compras.devolucion.ver',
            backend: 'A',
            descripcion:
              'Devoluciones a proveedor: aprobación, confirmación (sale del stock) y acreditación por NC.',
            api: ['/compras/devoluciones'],
            abre: { tipo: 'devoluciones-compra' },
          },
          {
            ruta: 'compras/guardados',
            nombre: 'Documentos guardados',
            permiso: 'compras.oc.ver',
            backend: 'A',
            alias: ['pendientes', 'guardados', 'borradores', 'documentos pendientes'],
            descripcion:
              'Las compras que quedaron guardadas sin terminar (órdenes de compra, recepciones, devoluciones a proveedor): tomarlas y terminar el proceso.',
            api: ['/documentos/guardados'],
            abre: { tipo: 'documentos-guardados', params: { ambito: 'compras' } },
          },
          {
            ruta: 'compras/aprobaciones',
            nombre: 'Aprobaciones de Compras',
            permiso: 'compras.oc.ver',
            backend: 'A',
            descripcion:
              'OC, reversas, excepciones de recepción, devoluciones e invalidaciones que esperan la firma de otra persona.',
            api: [
              '/compras/solicitudes-reversa',
              '/compras/excepciones-recepcion',
              '/compras/solicitudes-correccion',
            ],
            alias: ['aprobar oc', 'firmas compras'],
            abre: { tipo: 'aprobaciones-compras' },
          },
          {
            ruta: 'compras/proveedores',
            nombre: 'Proveedores',
            permiso: 'compras.oc.ver',
            backend: 'A',
            descripcion: 'Buscar proveedores y ver lo comprado a cada uno.',
            api: ['/entidades?rol=proveedor'],
            alias: ['buscar proveedor'],
            abre: { tipo: 'proveedores' },
          },
          {
            ruta: 'compras/esperando-nc',
            nombre: 'Esperando nota de crédito',
            permiso: 'tesoreria.ver',
            backend: 'A',
            descripcion:
              'Facturas con algo reclamado al proveedor (faltó, llegó roto, se devolvió): lo retenido del pago hasta que llegue la NC.',
            api: ['/tesoreria/cxp/documentos'],
            alias: ['pendiente nc', 'reclamos a proveedor', 'nota de credito pendiente'],
            abre: { tipo: 'esperando-nc' },
          },
          {
            ruta: 'compras/costos-logisticos',
            nombre: 'Costos logísticos',
            permiso: 'compras.politica.ver',
            backend: 'A',
            descripcion:
              'Cómo cobra el flete cada proveedor (en la línea o en un total) y el costo logístico de cada producto para repartirlo.',
            api: ['/compras/costos-logisticos'],
            alias: ['flete', 'costo logistico', 'reparto de flete', 'ccu'],
            abre: { tipo: 'costos-logisticos' },
          },
          {
            ruta: 'compras/costos',
            nombre: 'Costos de compra',
            permiso: 'compras.factura.ver',
            backend: 'A',
            descripcion:
              'Último costo por producto según facturas aceptadas, con el anterior, el rango y su historia.',
            api: ['/compras/costos'],
            alias: ['ultimo costo', 'precio de compra'],
            abre: { tipo: 'costos-compra' },
          },
          {
            ruta: 'compras/historial',
            nombre: 'Historial por proveedor',
            permiso: 'compras.oc.ver',
            backend: 'A',
            descripcion:
              'Todo lo comprado a un proveedor: OC, recepciones, facturas, NC/ND y devoluciones.',
            api: ['/compras/ordenes?proveedorEntidadId', '/compras/facturas'],
            abre: { tipo: 'historial-compras' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ VENTAS
  {
    id: 'ventas',
    nombre: 'Ventas',
    corto: 'Ventas',
    sigla: 'VE',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'ventas/punto-de-venta',
            nombre: 'Punto de Venta',
            permiso: 'caja.recibo.registrar',
            backend: 'B',
            accion: true,
            alias: ['meson', 'boleta', 'pos', 'caja', 'venta inmediata'],
            descripcion:
              'Venta de mesón: consumidor final, boleta o factura con su folio, pago inmediato (F10), vuelto.',
            api: ['/ventas/notas', '/caja/recibos', '/ventas/notas/:id/documentar'],
            habilitadas: ['Vender', 'Cobrar', 'Pedir boleta/factura'],
            pendientes: [
              'Entrega inmediata con salida de stock (en validación el despacho está bloqueado)',
            ],
            abre: { tipo: 'nota', params: { modo: 'meson' } },
          },
          {
            ruta: 'ventas/cotizacion',
            nombre: 'Cotización',
            permiso: 'ventas.cotizacion.ver',
            backend: 'A',
            descripcion:
              'Cotizaciones con el mismo cálculo de la NV: emitir, anular, imprimir y convertir en nota de venta.',
            api: ['/ventas/cotizaciones', '/ventas/cotizaciones/:id/convertir'],
            alias: ['cotizar', 'cov'],
            abre: { tipo: 'cotizaciones' },
          },
          {
            ruta: 'ventas/cotizacion-nueva',
            nombre: 'Nueva cotización',
            permiso: 'ventas.cotizacion.crear',
            backend: 'A',
            accion: true,
            oculta: true,
            descripcion: 'Cotización nueva para un cliente.',
            api: ['/ventas/cotizaciones'],
            alias: ['nueva cotizacion'],
            abre: { tipo: 'cotizacion' },
          },
          {
            ruta: 'ventas/factura-directa',
            nombre: 'Factura directa',
            permiso: 'ventas.nota.crear',
            backend: 'A',
            accion: true,
            alias: ['factura directa', 'fcv directa', 'facturar'],
            descripcion:
              'Factura sin pasar por una NV: se digita, se graba y se emite; al emitir se dice si la mercadería sale ahora o queda pendiente de despacho.',
            api: ['/ventas/notas', '/ventas/notas/:id/confirmar'],
            abre: { tipo: 'nota', params: { directo: 'FACTURA' } },
          },
          {
            ruta: 'ventas/boleta-directa',
            nombre: 'Boleta directa',
            permiso: 'ventas.nota.crear',
            backend: 'A',
            accion: true,
            alias: ['boleta directa', 'blv directa'],
            descripcion:
              'Boleta sin pasar por una NV: se digita, se graba y se emite; al emitir se dice si la mercadería sale ahora o queda pendiente de despacho.',
            api: ['/ventas/notas', '/ventas/notas/:id/confirmar'],
            abre: { tipo: 'nota', params: { directo: 'BOLETA' } },
          },
          {
            ruta: 'ventas/guia-directa',
            nombre: 'Guía de despacho directa',
            permiso: 'ventas.nota.crear',
            backend: 'A',
            accion: true,
            alias: ['guia directa', 'gdv directa', 'guía de despacho'],
            descripcion:
              'Mercadería que sale con guía y se factura después: se digita, se confirma y pasa al despacho (en preproducción el despacho está bloqueado).',
            api: ['/ventas/notas', '/ventas/notas/:id/confirmar', '/ventas/despachos'],
            abre: { tipo: 'nota', params: { directo: 'GUIA' } },
          },
          {
            ruta: 'ventas/nota-nueva',
            nombre: 'Nota de Venta',
            permiso: 'ventas.nota.crear',
            backend: 'A',
            accion: true,
            alias: ['nueva nota de venta', 'nueva nv', 'crear nota', 'pedido nuevo'],
            descripcion:
              'Venta comercial keyboard-first: cliente por código, crédito, RTU, escalas, firma en línea. Digita un N° para abrir otra NV.',
            api: ['/ventas/notas', '/ventas/notas/previsualizar', '/ventas/notas/:id/confirmar'],
            habilitadas: ['Crear', 'Previsualizar', 'Confirmar', 'Solicitar firma', 'Cobrar'],
            abre: { tipo: 'nota' },
            tecla: 'Alt+N',
          },
          {
            ruta: 'ventas/pedidos',
            nombre: 'Pedidos',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            descripcion:
              'Pedidos de todos los canales por estado, canal y vendedor («Mis pedidos»), con lo pendiente de despachar.',
            api: ['/ventas/notas?vendedorId', '/logistica/disponibles'],
            abre: { tipo: 'pedidos' },
          },
          {
            ruta: 'ventas/documentos',
            nombre: 'Facturas / Boletas',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['factura', 'boleta', 'reimprimir', 'documentos emitidos', 'folio'],
            descripcion:
              'Facturas, boletas y notas de crédito emitidas: folio, cliente, monto y estado SII; ver, imprimir y abrir el documento. La nota de venta es su origen.',
            api: ['/ventas/documentos-emitidos', '/ventas/documentos-emitidos/:id'],
            abre: { tipo: 'documentos-emitidos' },
          },
          {
            ruta: 'ventas/nota-credito',
            nombre: 'Nota de Crédito (directa)',
            permiso: 'ventas.nc.directa',
            backend: 'A',
            accion: true,
            alias: ['ncv', 'nota de credito', 'anular factura', 'anular boleta', 'devolucion'],
            descripcion:
              'Como la NCV de Random: busca la factura o boleta, elige el motivo (anular, devolver, corregir monto o texto) y, si la mercadería vuelve, a qué bodega entra. Con el permiso, sin firma (queda auditado).',
            api: ['/ventas/nc-directa', '/ventas/nc-directa/documentos'],
            abre: { tipo: 'nc-directa' },
          },
          {
            ruta: 'ventas/consignaciones',
            nombre: 'Consignaciones',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['consignacion', 'consignación', 'mercaderia en consignacion'],
            descripcion:
              'Mercadería donde el cliente: envíos y retornos con guía interna (GDI); se factura solo lo consumido.',
            api: ['/ventas/consignaciones'],
            abre: { tipo: 'consignaciones' },
          },
          {
            ruta: 'ventas/nc-nd',
            nombre: 'Notas de Crédito / Débito',
            permiso: 'ventas.devolucion.ver',
            backend: 'A',
            descripcion:
              'NC/ND de venta (clases A–E, con firma), devoluciones de clientes y reversas de despacho con su plan.',
            api: ['/ventas/notas-ajuste', '/ventas/devoluciones', '/ventas/reversas'],
            alias: ['devoluciones', 'nota de credito', 'ncv'],
            abre: { tipo: 'correcciones-venta' },
          },
          {
            ruta: 'ventas/guia',
            nombre: 'Guía de Despacho',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            descripcion:
              'Despachos de venta con su guía INTERNA (folio GDI, no tributaria, como en Random). La guía electrónica 52 llega con la certificación (D-127).',
            api: ['/ventas/despachos'],
            pendientes: ['GDV 52: decisión tributaria D-127 (Res. 154/2025)'],
            alias: ['gdv', 'guia', 'despachos'],
            abre: { tipo: 'despachos-venta' },
          },
          prep(
            'ventas/envio-correo',
            'Envío de documento por correo',
            'ventas.nota.ver',
            'C',
            'Enviar el PDF de un documento al cliente.',
            ['Correos productivos BLOQUEADOS en validación', 'Contrato de envío'],
            ui5,
          ),
          {
            ruta: 'ventas/vales',
            nombre: 'Vales de venta',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Vales de venta emitidos en el punto de venta: documento interno, no tributario (no va al SII). Reimpresión y su nota.',
            api: ['/ventas/vales', '/ventas/vales/:id'],
            alias: ['vale', 'vale interno', 'documento interno'],
            abre: { tipo: 'vales' },
          },
          {
            ruta: 'ventas/impresion-masiva',
            nombre: 'Impresión masiva de documentos',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Facturas, boletas y notas emitidas de un período: elegirlas y descargar sus PDF (original o cedible) en un ZIP para imprimir en lote.',
            api: ['/dte/documentos?desde&hasta', '/dte/documentos/lote-pdf'],
            alias: ['imprimir facturas', 'imprimir en lote'],
            abre: { tipo: 'impresion-masiva' },
          },
          {
            ruta: 'ventas/clientes',
            nombre: 'Clientes',
            permiso: 'entidades.ver',
            backend: 'A',
            alias: ['buscar cliente', 'ficha 360'],
            descripcion: 'Buscar clientes y abrir su Ficha 360.',
            api: ['/entidades', '/entidades/:id'],
            habilitadas: ['Buscar', 'Ficha 360', 'Solicitar cambio', 'Nueva NV para el cliente'],
            abre: { tipo: 'clientes' },
          },
          {
            ruta: 'ventas/precios',
            nombre: 'Precios por lista',
            permiso: 'precios.precios.ver',
            backend: 'A',
            alias: ['precios y escalas', 'tratamiento de listas', 'cambiar precio', 'f(x)'],
            descripcion:
              'Una lista en grilla: precio por unidad y por caja y sus escalas; se cambia en la celda.',
            api: ['/precios/listas/:id/tratamiento', '/precios/lote'],
            abre: { tipo: 'tratamiento-precios' },
          },
        ],
      },
      {
        nombre: 'Consultas',
        pantallas: [
          {
            ruta: 'ventas/documentos-random',
            nombre: 'Documentos de Random',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['factura random', 'buscar factura', 'folio', 'boleta random', 'histórico'],
            descripcion:
              'Facturas, boletas, guías, notas de crédito y notas de venta emitidas en Random: buscar por folio y verlas (solo consulta).',
            api: ['/historico/documentos'],
            abre: { tipo: 'documentos-random' },
          },
          {
            ruta: 'ventas/notas',
            nombre: 'Consulta de notas de venta',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['buscar documento', 'notas de venta'],
            descripcion: 'Notas de venta por estado, cliente, número y fecha.',
            api: ['/ventas/notas'],
            habilitadas: ['Buscar', 'Abrir', 'Línea de tiempo', 'Estado DTE'],
            abre: { tipo: 'notas' },
            tecla: 'Alt+O',
          },
          {
            ruta: 'ventas/guardados',
            nombre: 'Documentos guardados',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['pendientes', 'guardados', 'borradores', 'documentos pendientes'],
            descripcion:
              'Las ventas que quedaron guardadas sin terminar (notas de venta, facturas, boletas, guías, cotizaciones): tomarlas y terminar el proceso.',
            api: ['/documentos/guardados'],
            abre: { tipo: 'documentos-guardados', params: { ambito: 'ventas' } },
          },
          {
            ruta: 'ventas/firmas',
            nombre: 'Aprobaciones',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['pendientes de firma', 'firmas', 'autorizaciones', 'autorizar bloqueos'],
            descripcion:
              'Lo que espera autorización, con el porqué del bloqueo: marcar y autorizar varias de una vez (se actualiza sola).',
            api: ['/ventas/aprobaciones'],
            abre: { tipo: 'aprobaciones-ventas' },
          },
          {
            ruta: 'ventas/solicitudes-cambio',
            nombre: 'Solicitudes de cambio de ficha',
            permiso: 'entidades.ficha.aprobar_cambio',
            backend: 'A',
            descripcion: 'Cambios a la ficha de clientes que esperan aprobación del supervisor.',
            api: ['/entidades/solicitudes-cambio'],
            abre: { tipo: 'solicitudes' },
          },
          {
            ruta: 'ventas/resumen',
            nombre: 'Resumen de ventas',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            descripcion:
              'Ventas por día, cliente, producto, vendedor y canal (informes de ventas).',
            api: ['/informes/ventas.*'],
            abre: { tipo: 'informes', params: { modulo: 'ventas' } },
          },
        ],
      },
      {
        nombre: 'Caja',
        pantallas: [
          {
            ruta: 'ventas/mi-turno',
            nombre: 'Mi turno de caja',
            permiso: 'caja.turno.operar',
            backend: 'A',
            descripcion: 'Abrir turno, retiros e ingresos, cierre ciego.',
            api: ['/caja/turnos/actual', '/caja/turnos'],
            abre: { tipo: 'mi-turno' },
          },
          {
            ruta: 'ventas/turnos-caja',
            nombre: 'Turnos y cierres',
            permiso: 'caja.turno.ver',
            backend: 'A',
            descripcion: 'Revisión de los cierres de caja (conforme o con reparo).',
            api: ['/caja/turnos', '/caja/turnos/:id/revisar'],
            abre: { tipo: 'turnos-caja' },
          },
          {
            // 0130: con política de cierre, nadie revisa todos los cierres: solo las excepciones.
            ruta: 'ventas/excepciones-caja',
            nombre: 'Excepciones de cierre',
            // Cualquiera de los revisores (V3: lo que puede hacer cada uno sale de sus roles asignados).
            permiso:
              'caja.excepcion.resolver|caja.excepcion.resolver_tesoreria|caja.excepcion.resolver_nivel_dos|caja.excepcion.resolver_nivel_dos_alterno',
            backend: 'A',
            descripcion:
              'Cierres de caja con diferencia o incidentes y anulaciones posteriores: nivel 1 (soporte) y nivel 2 (aprobación superior); resolver o escalar.',
            alias: ['diferencia de caja', 'cierre con diferencia', 'excepcion de caja'],
            api: [
              '/caja/excepciones',
              '/caja/excepciones/:id/resolver',
              '/caja/excepciones/:id/escalar',
            ],
            abre: { tipo: 'excepciones-caja' },
          },
          {
            // 0130: una caja, una responsable, varios operadores (elegibles) y un solo arqueo. 0132: las
            // cajas (MESON-1…, TESORERIA) con su nombre; compartir una caja abierta no pide elegibles.
            ruta: 'ventas/operadores-caja',
            nombre: 'Cajas',
            permiso: 'caja.operadores.gestionar',
            backend: 'A',
            descripcion:
              'Las cajas de la empresa (mesones y tesorería) con su nombre y su política de cierre.',
            alias: [
              'operadores de caja',
              'caja compartida',
              'turno compartido',
              'politica de cierre',
              'mesones',
            ],
            api: [
              '/caja/puntos',
              '/caja/puntos/:id/configurar',
              '/caja/puntos/:id/elegibles',
              '/caja/puntos/:id/politica-cierre',
              '/caja/operadores/candidatos',
              '/caja/cuentas-compartidas',
            ],
            abre: { tipo: 'operadores-caja' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ PAGO DE CLIENTES
  // Como «Pago de Clientes» en Random: cobrar es una función de primer nivel, no un detalle de la
  // ficha del cliente.
  {
    id: 'cobros',
    nombre: 'Pago de Clientes',
    corto: 'Cobros',
    sigla: 'PC',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'cobros/cobrar-factura',
            nombre: 'Cobrar',
            permiso: 'cobranza.cobro.registrar',
            backend: 'A',
            accion: true,
            alias: [
              'cobrar factura',
              'pagar factura cliente',
              'pago a documento',
              'pago a documentos',
              'pago a estado de cuenta',
              'abono',
              'estado de cuenta',
              'cobro rapido',
              'cobro por folio',
            ],
            descripcion:
              'Cliente o folio; marcar una o varias facturas (una completa, otra abonada con lo que queda) y registrar el pago con sus medios. Lo que sobra queda a favor.',
            api: ['/tesoreria/cxc/documentos', '/tesoreria/cobros'],
            abre: { tipo: 'cobro', params: { modo: 'factura' } },
          },
          {
            ruta: 'cobros/aplicacion',
            nombre: 'Aplicación de pagos',
            permiso: 'cobranza.cobro.registrar',
            backend: 'A',
            accion: true,
            alias: ['aplicar pago', 'aplicar anticipo'],
            descripcion:
              'Aplicar el saldo a favor de un recibo (anticipo) a documentos del cliente.',
            api: ['/tesoreria/cxc/documentos', '/tesoreria/cobros'],
            abre: { tipo: 'cobro', params: { modo: 'aplicacion' } },
          },
          {
            ruta: 'cobros/anticipos',
            nombre: 'Anticipos',
            permiso: 'cobranza.cobro.registrar',
            backend: 'A',
            accion: true,
            alias: ['saldo a favor', 'anticipo'],
            descripcion:
              'Registrar un pago anticipado del cliente sin documento: queda como saldo a favor.',
            api: ['/tesoreria/cxc/documentos', '/tesoreria/cobros'],
            abre: { tipo: 'cobro', params: { modo: 'anticipo' } },
          },
        ],
      },
      {
        nombre: 'Consultas',
        pantallas: [
          {
            ruta: 'cobros/cartola',
            nombre: 'Cartola cliente',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: ['estado de cuenta cliente'],
            descripcion:
              'Cuenta corriente del cliente: documentos, NC y pagos con saldo corrido; imprimible.',
            api: ['/tesoreria/cxc/cartola'],
            abre: { tipo: 'cartola' },
          },
          {
            ruta: 'cobros/cxc',
            nombre: 'Cuentas por cobrar',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: ['cxc', 'deuda', 'cartera'],
            descripcion:
              'CxC por cliente: saldo, vencido, antigüedad por tramos y saldo a favor; Enter abre su cartola.',
            api: ['/tesoreria/cxc/resumen'],
            abre: { tipo: 'cxc' },
          },
          {
            ruta: 'cobros/cartola-vendedor',
            nombre: 'Cartola por vendedor',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: ['cartola general', 'morosos', 'mora', 'cartera vendedor'],
            descripcion:
              'Cuentas corrientes de los clientes que su ficha asigna al vendedor: quién está en mora y quién al día, por tramos; imprimible como en Random.',
            api: ['/tesoreria/cxc/por-vendedor'],
            abre: { tipo: 'cartola-vendedor' },
          },
          {
            ruta: 'cobros/vencimientos',
            nombre: 'Vencimientos',
            permiso: 'cobranza.ver',
            backend: 'A',
            descripcion: 'Documentos con saldo por fecha de vencimiento.',
            api: ['/tesoreria/cxc/documentos'],
            abre: { tipo: 'vencimientos' },
          },
          {
            ruta: 'cobros/cheques',
            nombre: 'Cartera de cheques',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: [
              'cheques',
              'depositar cheques',
              'cheques en cartera',
              'protesto',
              'cheque protestado',
            ],
            descripcion:
              'Cheques de clientes en cartera, depositados o protestados: depositar, confirmar el cobro (libera la línea del cliente) o protestar.',
            api: ['/tesoreria/cheques', '/tesoreria/cheques/estado'],
            abre: { tipo: 'cheques' },
          },
          {
            // docs/111 Etapa 2 (Rodrigo, 2026-10-25): la caja mayor.
            ruta: 'cobros/caja-tesoreria',
            nombre: 'Caja de tesorería',
            permiso: 'tesoreria.caja.operar',
            backend: 'A',
            alias: [
              'tesoreria',
              'caja mayor',
              'caja fuerte',
              'deposito',
              'depositar',
              'retiro de efectivo',
              'entrega de caja',
              'posicion de caja',
              'cuanta plata hay',
            ],
            descripcion:
              'La caja mayor: su turno del día, la posición de caja, las entregas de las cajas (contadas a ciegas), el depósito diario al banco y los retiros (para qué y a quién).',
            api: [
              '/tesoreria/caja',
              '/tesoreria/caja/posicion',
              '/tesoreria/caja/entregas',
              '/tesoreria/caja/depositos',
              '/tesoreria/caja/retiros',
            ],
            abre: { tipo: 'caja-tesoreria' },
          },
          {
            // docs/111 (Rodrigo, 2026-10-25): tesorería recibe lo cobrado en la ruta.
            ruta: 'cobros/recepcion-rutas',
            nombre: 'Recepción de rutas',
            permiso: 'tesoreria.recepcion.registrar',
            backend: 'A',
            alias: [
              'rendicion',
              'rendición de ruta',
              'recibir camion',
              'valores de ruta',
              'efectivo del chofer',
              'peoneta',
              'liquidacion del camion',
              'faltante de caja',
              'transferencias por verificar',
            ],
            descripcion:
              'Recibir los valores de las ODD rendidas: contar el efectivo a ciegas, marcar cheques y transferencias, y abonar todo a los clientes de una vez. Faltantes y sobrantes a cargo del recaudador.',
            api: [
              '/tesoreria/rutas/por-recibir',
              '/tesoreria/rutas/:id/liquidacion',
              '/tesoreria/rutas/:id/recibir',
              '/tesoreria/rutas/transferencias-por-verificar',
              '/tesoreria/rutas/faltantes',
            ],
            abre: { tipo: 'recepcion-rutas' },
          },
          {
            // Rodrigo, 2026-10-29: la ODD que no se rindió por la app la rinde tesorería a mano, con el
            // manifiesto en papel; las que ya están pagadas se cierran de una vez.
            ruta: 'cobros/rendicion-odd',
            nombre: 'Rendición de ODD',
            permiso: 'tesoreria.recepcion.registrar',
            backend: 'A',
            alias: [
              'rendir odd',
              'rendicion manual',
              'rendición a mano',
              'cerrar odd',
              'manifiesto',
              'hoja de rendicion',
              'odd en ruta',
              'cobro de ruta',
            ],
            descripcion:
              'Rendir a mano las ODD que no se rindieron por la app: marcar lo que volvió según el manifiesto, anotar lo cobrado, abonarlo a cada cliente y cerrar la ODD. Las ya pagadas se cierran de una vez.',
            api: [
              '/tesoreria/rutas/por-rendir',
              '/tesoreria/rutas/:id/hoja',
              '/tesoreria/rutas/:id/rendir-manual',
            ],
            abre: { tipo: 'rendicion-odd' },
          },
          {
            // Rodrigo, 2026-11-01: el efectivo que el vendedor recibió en la calle (comprobante CR-…)
            // queda en curso hasta que tesorería lo ingresa; aquí se ingresa, se anula y se audita.
            ruta: 'cobros/cobros-ruta',
            nombre: 'Cobros en ruta',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: [
              'cobros vendedores',
              'efectivo de vendedores',
              'comprobante de recepcion',
              'pago en curso',
              'entrega de vendedores',
              'auditar vendedor',
              'cobro en terreno',
            ],
            descripcion:
              'El efectivo que los vendedores recibieron en ruta con su comprobante: ingresarlo a tesorería (crea el recibo y aplica el pago), anular un comprobante mal hecho y auditar lo de cada vendedor.',
            api: [
              '/tesoreria/cobros-ruta',
              '/tesoreria/cobros-ruta/ingresar',
              '/tesoreria/cobros-ruta/:id/anular',
            ],
            abre: { tipo: 'cobros-ruta' },
          },
          {
            // Como la «Teneduría de Pagos» de Random (Rodrigo, 2026-10-28): el lugar donde tesorería
            // revisa y corrige los cobros ya hechos, sin pasar por la nota de venta.
            ruta: 'cobros/historial',
            nombre: 'Teneduría de pagos',
            permiso: 'cobranza.ver',
            backend: 'A',
            alias: [
              'historial',
              'historial de cobros',
              'recibos',
              'cobros',
              'teneduria',
              'teneduría de pagos',
              'anular recibo',
              'corregir medio de pago',
              'saldo a favor',
            ],
            descripcion:
              'Todos los recibos (mesón y cobranza) con sus medios, aplicaciones y turno: corregir con qué se cobró, anular, reversar aplicaciones y aplicar saldos a favor. Todo con motivo y auditado.',
            api: [
              '/tesoreria/cobros',
              '/caja/recibos/:id/medios',
              '/caja/recibos/:id/anular',
              '/caja/solicitudes-anulacion',
              '/caja/solicitudes-anulacion/:id/rechazar',
              '/tesoreria/aplicaciones/:id/reversar',
            ],
            abre: { tipo: 'historial-cobros' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ PAGO A PROVEEDORES
  {
    id: 'pagos',
    nombre: 'Pago a Proveedores',
    corto: 'Pagos',
    sigla: 'PP',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'pagos/pagar-factura',
            nombre: 'Pagar',
            permiso: 'tesoreria.pago.solicitar',
            backend: 'A',
            accion: true,
            alias: [
              'pagar proveedor',
              'pagar factura',
              'pago a documentos proveedor',
              'abono proveedor',
              'pago a estado de cuenta proveedor',
            ],
            descripcion:
              'Proveedor, una o varias facturas (una completa, otra abonada) y solicitar el pago; lo autoriza otra persona y se registra pagado con comprobante.',
            api: ['/tesoreria/cxp/documentos', '/tesoreria/pagos'],
            abre: { tipo: 'pago', params: { modo: 'factura' } },
          },
          {
            ruta: 'pagos/pago-masivo',
            nombre: 'Pago masivo',
            permiso: 'tesoreria.pago.solicitar',
            backend: 'A',
            accion: true,
            alias: ['nomina de pago', 'pagos masivos'],
            descripcion: 'Documentos de varios proveedores: un pago por proveedor en un lote.',
            api: ['/tesoreria/cxp/documentos', '/tesoreria/pagos'],
            abre: { tipo: 'pago', params: { modo: 'masivo' } },
          },
          {
            ruta: 'pagos/anticipos',
            nombre: 'Anticipos',
            permiso: 'tesoreria.pago.solicitar',
            backend: 'A',
            accion: true,
            alias: ['anticipo proveedor'],
            descripcion:
              'Pago a un proveedor sin documento: queda como anticipo y se aplica después.',
            api: ['/tesoreria/cxp/documentos', '/tesoreria/pagos'],
            abre: { tipo: 'pago', params: { modo: 'anticipo' } },
          },
          {
            ruta: 'pagos/autorizaciones',
            nombre: 'Autorizaciones',
            permiso: 'tesoreria.ver',
            backend: 'A',
            alias: ['autorizar pagos'],
            descripcion:
              'Pagos por autorizar (otra persona que quien solicita) y autorizados por registrar como pagados.',
            api: ['/tesoreria/pagos'],
            abre: { tipo: 'autorizaciones-pago' },
          },
          {
            ruta: 'pagos/cuentas-bancarias',
            nombre: 'Cuentas bancarias',
            permiso: 'tesoreria.ver',
            backend: 'A',
            alias: ['cuenta corriente empresa', 'bancos'],
            descripcion:
              'Cuentas bancarias de la empresa: desde cuál se transfiere y con cuál se giran los cheques.',
            api: ['/tesoreria/cuentas-bancarias'],
            abre: { tipo: 'cuentas-bancarias' },
          },
        ],
      },
      {
        nombre: 'Consultas',
        pantallas: [
          {
            ruta: 'pagos/cartola',
            nombre: 'Cartola proveedor',
            permiso: 'tesoreria.ver',
            backend: 'A',
            descripcion: 'Cuenta corriente del proveedor: facturas, NC y pagos con saldo corrido.',
            api: ['/tesoreria/cxp/cartola'],
            abre: { tipo: 'cartola-proveedor' },
          },
          {
            ruta: 'pagos/cxp',
            nombre: 'Cuentas por pagar',
            permiso: 'tesoreria.ver',
            backend: 'A',
            alias: ['cxp', 'deuda proveedores'],
            descripcion:
              'CxP por proveedor: saldo, vencido, en pagos en curso y anticipos; Enter abre la cartola.',
            api: ['/tesoreria/cxp/resumen'],
            abre: { tipo: 'cxp' },
          },
          {
            ruta: 'pagos/historial',
            nombre: 'Historial',
            permiso: 'tesoreria.ver',
            backend: 'A',
            descripcion: 'Pagos con su estado, comprobante y documentos; anulación con motivo.',
            api: ['/tesoreria/pagos'],
            abre: { tipo: 'historial-pagos' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ OPERACIONES
  // Definición propia MADECI: NV → OD → camión → manifiesto → preparación → validación → aprobación
  // → facturación masiva → documentos → hoja de rendición → ruta → entrega → rendición. No se factura
  // lo que no está en el camión.
  {
    id: 'operaciones',
    nombre: 'Operaciones',
    corto: 'Operaciones',
    sigla: 'OP',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'operaciones/od',
            nombre: 'Órdenes de Despacho',
            permiso: 'logistica.ver',
            backend: 'A',
            alias: ['od', 'ordenes'],
            descripcion:
              'Las ODD con su camión, chofer, ruta, notas y estado; Enter abre la ODD para trabajarla.',
            api: ['/logistica/ordenes'],
            abre: { tipo: 'ordenes-despacho', params: { estados: '' } },
          },
          {
            ruta: 'operaciones/od-nueva',
            nombre: 'Nueva ODD',
            permiso: 'logistica.od.crear',
            backend: 'A',
            accion: true,
            alias: [
              'crear od',
              'nueva orden de despacho',
              'nv por despachar',
              'nv disponibles',
              'despachos',
              'planificador',
            ],
            descripcion:
              'NV por despachar → Orden de Despacho nueva: las NV confirmadas que aún no están en una ODD; filtrar, elegir varias, camión, chofer, ruta y fecha.',
            api: ['/logistica/disponibles', '/logistica/ordenes'],
            abre: { tipo: 'od' },
          },
          {
            ruta: 'operaciones/preparacion',
            nombre: 'Preparación de Cargas',
            permiso: 'logistica.ver',
            backend: 'A',
            alias: ['preparar carga'],
            descripcion:
              'ODD por preparar y validar: esperado / preparado / diferencia y segunda validación antes de cargar.',
            api: ['/logistica/ordenes'],
            abre: {
              tipo: 'ordenes-despacho',
              params: { estados: 'PLANIFICADA,EN_PREPARACION,VALIDADA' },
            },
          },
          {
            ruta: 'operaciones/manifiestos',
            nombre: 'Manifiestos',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion:
              'Manifiesto de cada ODD: consolidado por producto y desglose por NV y cliente, imprimible.',
            api: ['/logistica/ordenes'],
            abre: {
              tipo: 'ordenes-despacho',
              params: { estados: 'EN_PREPARACION,VALIDADA,CARGADA,APROBADA,FACTURADA,EN_RUTA' },
            },
          },
          {
            ruta: 'operaciones/facturacion',
            nombre: 'Facturación de Cargas',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion:
              'Cargas por aprobar y facturar: un DTE por NV con el motor propio; solo se factura lo que está en el camión.',
            api: ['/logistica/ordenes'],
            abre: { tipo: 'ordenes-despacho', params: { estados: 'CARGADA,APROBADA,FACTURADA' } },
          },
          {
            ruta: 'operaciones/rutas',
            nombre: 'Rutas',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion: 'ODD facturadas por salir y en ruta: entregas por parada y cobros.',
            api: ['/logistica/ordenes'],
            abre: { tipo: 'ordenes-despacho', params: { estados: 'FACTURADA,EN_RUTA' } },
          },
          {
            ruta: 'operaciones/rendiciones',
            nombre: 'Rendiciones',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion:
              'ODD en ruta o rendidas: hoja de rendición, descarga de lo no entregado y cierre.',
            api: ['/logistica/ordenes'],
            abre: { tipo: 'ordenes-despacho', params: { estados: 'EN_RUTA,RENDIDA' } },
          },
          {
            ruta: 'operaciones/incidencias',
            nombre: 'Incidencias',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion:
              'Bandeja de todas las ODD: incidencias de carga o facturación, rechazos y entregas parciales que piden NC, y pasos fallidos.',
            api: ['/logistica/incidencias'],
            abre: { tipo: 'incidencias-od' },
          },
        ],
      },
      {
        nombre: 'Lo que existe hoy (sin ODD)',
        pantallas: [
          {
            ruta: 'operaciones/despacho-nota',
            nombre: 'Despacho por nota',
            permiso: 'ventas.despacho.ver',
            backend: 'A',
            descripcion: 'Despacho de UNA nota: parcial o total, con amparo tributario (D-132).',
            api: ['/ventas/despachos', '/ventas/despachos/:id/confirmar'],
            habilitadas: ['Buscar la nota y despachar'],
            abre: { tipo: 'notas', params: { vista: 'despacho' } },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ DTE
  {
    id: 'dte',
    nombre: 'DTE',
    corto: 'DTE',
    sigla: 'DT',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'dte/random',
            nombre: 'Documentos de Random',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['factura random', 'buscar factura', 'folio', 'boleta random', 'histórico'],
            descripcion:
              'Facturas, boletas, guías, notas de crédito y notas de venta emitidas en Random: buscar por folio y verlas (solo consulta).',
            api: ['/historico/documentos'],
            abre: { tipo: 'documentos-random' },
          },
          {
            ruta: 'dte/documentos',
            nombre: 'Documentos',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Todos los DTE de la empresa por período, tipo, folio y estado; PDF, XML y su nota de origen.',
            api: ['/dte/documentos', '/dte/documentos/:id/pdf', '/dte/documentos/:id/xml'],
            abre: { tipo: 'dte', params: { modo: 'documentos' } },
          },
          ...(
            [
              ['facturas', 'Facturas', '33 · 34'],
              ['boletas', 'Boletas', '39 · 41'],
              ['guias', 'Guías', '52'],
              ['nc', 'Notas de Crédito', '61'],
              ['nd', 'Notas de Débito', '56'],
            ] as const
          ).map(([r, n, t]): Pantalla => ({
            ruta: `dte/${r}`,
            nombre: n,
            permiso: 'dte.ver',
            backend: 'A',
            descripcion: `${n} (tipo ${t}).`,
            api: ['/dte/documentos?tipoDte'],
            abre: { tipo: 'dte', params: { modo: 'documentos', tipo: t.slice(0, 2) } },
          })),
        ],
      },
      {
        nombre: 'Control',
        pantallas: [
          {
            ruta: 'dte/estado-sii',
            nombre: 'Estado SII',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Estado del documento, del envío y del SII de cada DTE; reintento de los fallidos.',
            api: ['/dte/documentos', '/dte/documentos/:id/reintentar'],
            abre: { tipo: 'dte', params: { modo: 'estado-sii' } },
          },
          {
            ruta: 'dte/publicacion',
            nombre: 'Publicación al SII',
            permiso: 'dte.ver',
            backend: 'A',
            alias: ['apagar publicacion', 'pausa sii', 'motor sii', 'cola sii'],
            descripcion:
              'Interruptores de publicación al SII: facturas/NC y boletas por separado; lo emitido con la publicación apagada queda en cola para corregir.',
            api: ['/dte/publicacion', '/dte/publicacion/cola'],
            abre: { tipo: 'dte', params: { modo: 'publicacion' } },
          },
          {
            ruta: 'dte/monitor',
            nombre: 'Monitor',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Motor propio: listo para folios (o CAF pendientes), certificado, folios por tipo y bloqueos de producción.',
            api: ['/dte/motor/estado', '/dte/readiness'],
            abre: { tipo: 'dte', params: { modo: 'monitor' } },
          },
          {
            ruta: 'dte/reimpresion',
            nombre: 'Reimpresión',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion: 'PDF original y CEDIBLE de documentos emitidos.',
            api: ['/dte/documentos/:id/pdf'],
            abre: { tipo: 'dte', params: { modo: 'reimpresion' } },
          },
          {
            ruta: 'dte/formato',
            nombre: 'Formato de factura',
            permiso: 'dte.ver',
            backend: 'A',
            alias: ['sucursales', 'cuenta corriente', 'formato impresion', 'pie de factura'],
            descripcion:
              'Lo que la empresa agrega a su factura impresa: sucursales, correo y teléfono arriba; cuentas corrientes y condiciones abajo.',
            api: ['/dte/formato-impresion'],
            abre: { tipo: 'config', params: { maestro: 'formato-factura' } },
          },
          {
            ruta: 'dte/folios',
            nombre: 'Certificado y folios',
            permiso: 'dte.caf.ver_metadata',
            backend: 'A',
            descripcion:
              'Motor propio: cargar y activar el certificado digital y los CAF del SII (las llaves quedan cifradas en la custodia); folios usados y anulados.',
            api: [
              '/dte/certificados',
              '/dte/caf',
              '/dte/caf/nativo',
              '/dte/caf/:id/estado-nativo',
              '/dte/folios',
            ],
            abre: { tipo: 'dte', params: { modo: 'folios' } },
          },
          {
            ruta: 'dte/incidencias',
            nombre: 'Incidencias',
            permiso: 'dte.ver',
            backend: 'A',
            descripcion:
              'Documentos fallidos, en conflicto o rechazados y la reconciliación de folios.',
            api: ['/dte/documentos?estado', '/dte/reconciliacion'],
            abre: { tipo: 'dte', params: { modo: 'incidencias' } },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ INFORMES
  {
    id: 'informes',
    nombre: 'Informes',
    corto: 'Informes',
    sigla: 'IF',
    secciones: [
      {
        pantallas: (
          [
            [
              'stock',
              'Stock',
              'inventario.ver',
              'existencias valorizadas, sin movimiento, movimientos',
            ],
            ['compras', 'Compras', 'compras.oc.ver', 'por proveedor, por producto, OC abiertas'],
            ['ventas', 'Ventas', 'ventas.nota.ver', 'por día, cliente, producto, vendedor y canal'],
            ['clientes', 'Clientes', 'entidades.ver', 'cartera y clientes sin compra'],
            ['proveedores', 'Proveedores', 'compras.oc.ver', 'proveedores y lo comprado'],
            [
              'operaciones',
              'Operaciones',
              'ventas.despacho.ver',
              'órdenes de despacho y despachos',
            ],
            ['cobranza', 'Cobranza', 'cobranza.ver', 'recaudación por día y medio'],
            ['pagos', 'Pagos', 'tesoreria.ver', 'pagos a proveedores'],
            ['dte', 'DTE', 'dte.ver', 'emitidos y con problema'],
            ['rentabilidad', 'Rentabilidad', 'valorizacion.ver', 'margen por producto (estimado)'],
          ] as const
        ).map(([r, n, p, d]): Pantalla => ({
          ruta: `informes/${r}`,
          nombre: n,
          permiso: p,
          backend: 'A',
          descripcion: `Informes de ${n.toLowerCase()}: ${d}. Exportables a CSV e imprimibles.`,
          api: ['/informes/catalogo', `/informes/${r}.*`],
          alias: [`informe de ${n.toLowerCase()}`],
          abre: { tipo: 'informes', params: { modulo: r } },
        })),
      },
    ],
  },
  // ------------------------------------------------------------------ GERENCIA
  // Pedido de Rodrigo (2026-10-25): los indicadores del negocio en vivo, solo para quien decida.
  {
    id: 'gerencia',
    nombre: 'Gerencia',
    corto: 'Gerencia',
    sigla: 'GE',
    secciones: [
      {
        pantallas: [
          {
            ruta: 'gerencia/panel',
            nombre: 'Panel del negocio',
            permiso: 'gerencia.panel.ver',
            backend: 'A',
            descripcion:
              'Venta y margen del día y del mes (comparados con la semana y el mes anterior, con la historia de Random), efectivo en cajas, cobrado hoy, cheques en cartera, CxC, CxP y proyección de flujo a 13 semanas. Se actualiza solo cada minuto.',
            api: ['/gerencia/panel'],
            alias: [
              'indicadores',
              'panel',
              'dashboard',
              'flujo de caja',
              'inteligencia de negocio',
            ],
            abre: { tipo: 'panel-negocio' },
          },
        ],
      },
    ],
  },
  // ------------------------------------------------------------------ CONFIGURACIÓN
  {
    id: 'configuracion',
    nombre: 'Configuración',
    corto: 'Configuración',
    sigla: 'CF',
    secciones: [
      {
        nombre: 'Productos',
        pantallas: [
          {
            ruta: 'configuracion/productos',
            nombre: 'Productos',
            permiso: 'catalogo.productos.ver',
            backend: 'A',
            descripcion: 'Catálogo único del grupo.',
            api: ['/productos'],

            abre: { tipo: 'config', params: { maestro: 'productos' } },
          },
          {
            ruta: 'configuracion/presentaciones',
            nombre: 'Presentaciones',
            permiso: 'catalogo.productos.ver',
            backend: 'A',
            descripcion: 'Presentaciones (RTU) y factor a unidad base.',
            api: ['/productos/:id/presentaciones'],

            abre: { tipo: 'config', params: { maestro: 'productos', foco: 'presentaciones' } },
          },
          {
            ruta: 'configuracion/codigos',
            nombre: 'Códigos',
            permiso: 'catalogo.productos.ver',
            backend: 'A',
            descripcion: 'Códigos internos, de barra y de proveedor.',
            api: ['/productos/:id/codigos'],

            abre: { tipo: 'config', params: { maestro: 'productos', foco: 'codigos' } },
          },
          {
            ruta: 'configuracion/categorias',
            nombre: 'Categorías',
            permiso: 'catalogo.clasificacion.ver',
            backend: 'A',
            descripcion: 'Árbol súper familia › familia › subfamilia (como en Random).',
            api: ['/catalogo/categorias'],

            abre: { tipo: 'config', params: { maestro: 'categorias' } },
          },
          {
            ruta: 'configuracion/marcas',
            nombre: 'Marcas',
            permiso: 'catalogo.clasificacion.ver',
            backend: 'A',
            descripcion: 'Marcas de producto.',
            api: ['/catalogo/marcas'],

            abre: { tipo: 'config', params: { maestro: 'marcas' } },
          },
        ],
      },
      {
        nombre: 'Comercial',
        pantallas: [
          {
            ruta: 'configuracion/listas',
            nombre: 'Listas de precios',
            permiso: 'precios.listas.ver',
            backend: 'A',
            descripcion: 'Listas de venta y costo por empresa.',
            api: ['/precios/listas'],

            abre: { tipo: 'config', params: { maestro: 'listas' } },
          },
          {
            ruta: 'configuracion/tratamiento-listas',
            nombre: 'Tratamiento de listas',
            permiso: 'precios.precios.ver',
            backend: 'A',
            descripcion:
              'Precios de una lista por súper familia, familia, subfamilia y marca; ajuste por % y escalas.',
            api: ['/precios/listas/:id/tratamiento', '/precios/lote'],

            abre: { tipo: 'tratamiento-precios' },
          },
          {
            ruta: 'configuracion/escalas',
            nombre: 'Escalas',
            permiso: 'precios.precios.ver',
            backend: 'A',
            descripcion: 'Escalas de precio por cantidad (D-50).',
            api: ['/precios/:id/escalas'],

            abre: { tipo: 'config', params: { maestro: 'listas', foco: 'escalas' } },
          },
          {
            ruta: 'configuracion/vendedores',
            nombre: 'Vendedores',
            permiso: 'entidades.ver',
            backend: 'A',
            descripcion: 'Vendedores de la empresa.',
            api: ['/vendedores'],

            abre: { tipo: 'config', params: { maestro: 'vendedores' } },
          },
          {
            ruta: 'configuracion/zonas',
            nombre: 'Zonas',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            descripcion: 'Zonas de despacho y sus comunas.',
            api: ['/ventas/zonas'],

            abre: { tipo: 'config', params: { maestro: 'zonas' } },
          },
          {
            ruta: 'configuracion/costo-logistico',
            nombre: 'Costo logístico',
            permiso: 'ventas.nota.ver',
            backend: 'A',
            alias: ['flete', 'recargo logistico', 'logistica', 'fle_mad'],
            descripcion:
              'Costo logístico por producto (como en Random): monto por unidad que al facturar pasa a la línea LOGISTICA; carga masiva por % de la lista.',
            api: ['/ventas/costo-logistico'],
            abre: { tipo: 'config', params: { maestro: 'costo-logistico' } },
          },
          {
            ruta: 'configuracion/camiones',
            nombre: 'Camiones',
            permiso: 'logistica.ver',
            backend: 'A',
            alias: ['vehiculos', 'patentes'],
            descripcion: 'Camiones de reparto: patente y su ubicación CAMION del inventario.',
            api: ['/logistica/vehiculos'],
            abre: { tipo: 'maestro-logistica', params: { tipo: 'camiones' } },
          },
          {
            ruta: 'configuracion/choferes',
            nombre: 'Choferes y peonetas',
            permiso: 'logistica.ver',
            alias: ['chofer', 'peoneta', 'recaudador', 'personal de ruta'],
            backend: 'A',
            descripcion:
              'Personal de ruta: choferes (vehículo y carga) y peonetas recaudadores (ruta y cobranza).',
            api: ['/logistica/choferes'],
            abre: { tipo: 'maestro-logistica', params: { tipo: 'choferes' } },
          },
          {
            ruta: 'configuracion/rutas',
            nombre: 'Rutas',
            permiso: 'logistica.ver',
            backend: 'A',
            descripcion: 'Rutas de reparto: conjuntos de zonas logísticas.',
            api: ['/logistica/rutas'],
            abre: { tipo: 'maestro-logistica', params: { tipo: 'rutas' } },
          },
          {
            ruta: 'configuracion/condiciones',
            nombre: 'Condiciones de pago',
            permiso: 'ventas.politica.ver',
            backend: 'A',
            descripcion:
              'Condiciones de pago permitidas por canal (contado, cheque, crédito, transferencia), plazo máximo y cuáles consumen cupo: política de crédito.',
            api: ['/ventas/politicas', '/ventas/politicas/credito'],
            abre: { tipo: 'config', params: { maestro: 'politicas', foco: 'condiciones' } },
          },
          {
            ruta: 'configuracion/impuestos',
            nombre: 'Impuestos',
            permiso: 'tributario.impuestos.ver',
            backend: 'A',
            descripcion: 'IVA, ILA y adicionales, con sus tasas.',
            api: ['/tributario/impuestos', '/tributario/tasas'],

            abre: { tipo: 'config', params: { maestro: 'impuestos' } },
          },
        ],
      },
      {
        nombre: 'Organización',
        pantallas: [
          {
            ruta: 'configuracion/bodegas',
            nombre: 'Bodegas',
            permiso: 'inventario.ver',
            backend: 'A',
            descripcion: 'Sucursales y ubicaciones de inventario.',
            api: ['/empresas/:id/ubicaciones'],

            abre: { tipo: 'config', params: { maestro: 'bodegas' } },
          },
          {
            ruta: 'configuracion/empresas',
            nombre: 'Empresas',
            permiso: 'organizacion.empresas.ver',
            backend: 'A',
            descripcion: 'Empresas del grupo.',
            api: ['/empresas'],

            abre: { tipo: 'config', params: { maestro: 'empresas' } },
          },
          {
            ruta: 'configuracion/usuarios',
            nombre: 'Usuarios',
            permiso: 'organizacion.usuarios.ver',
            backend: 'A',
            descripcion: 'Usuarios, invitaciones y roles por empresa.',
            api: ['/usuarios'],

            abre: { tipo: 'config', params: { maestro: 'usuarios' } },
          },
          {
            ruta: 'configuracion/roles',
            nombre: 'Roles',
            permiso: 'organizacion.roles.ver',
            backend: 'A',
            descripcion: 'Perfiles y sus permisos.',
            api: ['/roles'],

            abre: { tipo: 'config', params: { maestro: 'roles' } },
          },
          {
            ruta: 'configuracion/permisos',
            nombre: 'Permisos',
            permiso: 'organizacion.roles.ver',
            backend: 'A',
            descripcion: 'Catálogo de permisos.',
            api: ['/permisos'],

            abre: { tipo: 'config', params: { maestro: 'permisos' } },
          },
          {
            ruta: 'configuracion/aprobaciones',
            nombre: 'Flujos de aprobación',
            permiso: 'ventas.politica.ver',
            backend: 'A',
            descripcion: 'Políticas de aprobación, reserva y crédito.',
            api: ['/ventas/politicas', '/compras/politicas'],

            abre: { tipo: 'config', params: { maestro: 'politicas', foco: 'aprobaciones' } },
          },
          {
            ruta: 'configuracion/parametros',
            nombre: 'Parámetros',
            permiso: 'organizacion.parametros.ver',
            backend: 'A',
            descripcion: 'Parámetros de la empresa.',
            api: ['/parametros'],

            abre: { tipo: 'config', params: { maestro: 'parametros' } },
          },
          {
            ruta: 'configuracion/integraciones',
            nombre: 'Integraciones',
            permiso: 'importacion.ver',
            backend: 'B',
            descripcion:
              'Importación del sistema anterior: RETIRADA del runtime (RANDOM_RUNTIME_DEPENDENCY = 0). Solo se habilita para una migración controlada con MADECI_IMPORTACION_LEGADO=habilitada.',
            api: ['/importacion/corridas'],
            pendientes: [
              'N/A en operación: la importación es una herramienta de migración, no del día a día',
            ],
            checkpoint: 'UI-5',
          },
          {
            ruta: 'configuracion/api-keys',
            nombre: 'API keys',
            permiso: 'organizacion.api_keys.gestionar',
            backend: 'A',
            descripcion: 'Llaves de integración.',
            api: ['/api-keys'],

            abre: { tipo: 'config', params: { maestro: 'api-keys' } },
          },
          {
            ruta: 'configuracion/auditoria',
            nombre: 'Auditoría',
            permiso: 'auditoria.ver',
            backend: 'A',
            descripcion: 'Quién hizo qué, cuándo y con qué motivo.',
            api: ['/auditoria', '/eventos'],

            abre: { tipo: 'config', params: { maestro: 'auditoria' } },
          },
          {
            ruta: 'configuracion/mapa',
            nombre: 'Mapa del ERP (A/B/C)',
            backend: 'A',
            descripcion:
              'Matriz de funciones, rutas, estado del backend y API (solo VALIDACIÓN/DEV).',
            abre: { tipo: 'mapa' },
          },
        ],
      },
    ],
  },
];

/**
 * Cómo se reparte cada subsistema en el menú de texto de arriba (como Random): el ícono de la barra
 * elige el subsistema; arriba aparecen SUS menús; cada menú se despliega en grupos con su detalle.
 * Toda función visible debe estar en algún grupo (lo verifica una prueba).
 */
const DISPOSICION: Record<string, [menu: string, grupos: [grupo: string, rutas: string[]][]][]> = {
  ventas: [
    [
      'Negocios',
      [
        ['Inicio de negocios', ['ventas/cotizacion']],
        ['Formalización', ['ventas/nota-nueva', 'ventas/pedidos']],
        [
          'Documentos directos',
          ['ventas/factura-directa', 'ventas/boleta-directa', 'ventas/guia-directa'],
        ],
      ],
    ],
    [
      'Punto de Venta',
      [
        ['Mesón', ['ventas/punto-de-venta']],
        [
          'Caja',
          [
            'ventas/mi-turno',
            'ventas/turnos-caja',
            'ventas/excepciones-caja',
            'ventas/operadores-caja',
          ],
        ],
      ],
    ],
    [
      'Documentos',
      [
        [
          'Operación de ventas',
          [
            'ventas/guia',
            'ventas/consignaciones',
            'ventas/documentos',
            'ventas/vales',
            'ventas/nota-credito',
            'ventas/nc-nd',
          ],
        ],
        ['Comunicaciones', ['ventas/envio-correo', 'ventas/impresion-masiva']],
      ],
    ],
    [
      'Clientes y Precios',
      [
        ['Clientes', ['ventas/clientes', 'ventas/solicitudes-cambio']],
        ['Precios', ['ventas/precios']],
      ],
    ],
    [
      'Consultas',
      [
        ['Notas de venta', ['ventas/notas', 'ventas/guardados', 'ventas/firmas']],
        ['Resumen', ['ventas/resumen']],
        ['Random (histórico)', ['ventas/documentos-random']],
      ],
    ],
  ],
  compras: [
    [
      'Registro de compras',
      [['SII y XML recibidos', ['compras/registro-compras', 'compras/recibidos']]],
    ],
    [
      'Abastecimiento',
      [
        ['Órdenes de compra', ['compras/oc-nueva', 'compras/ordenes']],
        ['Aprobaciones', ['compras/guardados', 'compras/aprobaciones']],
      ],
    ],
    [
      'Recepción',
      [
        [
          'Recepción de mercadería',
          ['compras/recepciones', 'compras/recepcion-sin-oc', 'compras/ingreso-sin-factura'],
        ],
        [
          'Documentos del proveedor',
          ['compras/factura-nueva', 'compras/facturas', 'compras/notas'],
        ],
        ['Devoluciones', ['compras/devoluciones', 'compras/esperando-nc']],
      ],
    ],
    [
      'Proveedores',
      [['Proveedores', ['compras/proveedores', 'compras/costos-logisticos', 'compras/costos']]],
    ],
    ['Consultas', [['Historial', ['compras/historial']]]],
  ],
  stock: [
    [
      'Consultas',
      [
        ['Existencias', ['stock/consulta', 'stock/por-bodega']],
        ['Compromisos', ['stock/reservas']],
      ],
    ],
    [
      'Movimientos',
      [
        ['Movimientos internos', ['stock/transferencias', 'stock/ajustes']],
        ['Inventario', ['stock/fisico']],
      ],
    ],
    [
      'Kardex y Costos',
      [
        ['Libro de movimientos', ['stock/movimientos', 'stock/kardex']],
        ['Costos', ['stock/valorizacion']],
      ],
    ],
  ],
  cobros: [
    [
      'Cobranza',
      [
        ['Cobrar', ['cobros/cobrar-factura']],
        // docs/111 · tesorería: la caja mayor y lo cobrado en la ruta.
        [
          'Tesorería',
          [
            'cobros/caja-tesoreria',
            'cobros/rendicion-odd',
            'cobros/recepcion-rutas',
            'cobros/cobros-ruta',
          ],
        ],
      ],
    ],
    ['Aplicación', [['Aplicación de pagos', ['cobros/aplicacion', 'cobros/anticipos']]]],
    [
      'Consultas',
      [
        ['Cuenta corriente', ['cobros/cartola', 'cobros/cxc', 'cobros/cartola-vendedor']],
        ['Seguimiento', ['cobros/vencimientos', 'cobros/cheques', 'cobros/historial']],
      ],
    ],
  ],
  pagos: [
    ['Pagos', [['Pagar', ['pagos/pagar-factura', 'pagos/pago-masivo']]]],
    [
      'Control',
      [
        [
          'Control de pagos',
          ['pagos/anticipos', 'pagos/autorizaciones', 'pagos/cuentas-bancarias'],
        ],
      ],
    ],
    [
      'Consultas',
      [
        ['Cuenta corriente', ['pagos/cartola', 'pagos/cxp']],
        ['Seguimiento', ['pagos/historial']],
      ],
    ],
  ],
  operaciones: [
    ['Órdenes de Despacho', [['Orden de despacho', ['operaciones/od-nueva', 'operaciones/od']]]],
    [
      'Carga',
      [
        ['Preparación', ['operaciones/preparacion', 'operaciones/manifiestos']],
        ['Facturación', ['operaciones/facturacion']],
      ],
    ],
    [
      'Ruta y Rendición',
      [
        ['En ruta', ['operaciones/rutas']],
        ['Cierre', ['operaciones/rendiciones', 'operaciones/incidencias']],
      ],
    ],
    ['Hoy (sin ODD)', [['Lo que existe hoy', ['operaciones/despacho-nota']]]],
  ],
  dte: [
    [
      'Documentos',
      [
        ['Emitidos', ['dte/documentos']],
        ['Por tipo', ['dte/facturas', 'dte/boletas', 'dte/guias', 'dte/nc', 'dte/nd']],
        ['Emitidos en Random', ['dte/random']],
      ],
    ],
    [
      'Motor propio',
      [['Certificado, folios y publicación', ['dte/folios', 'dte/publicacion', 'dte/monitor']]],
    ],
    [
      'SII',
      [
        ['Estado', ['dte/estado-sii']],
        ['Problemas', ['dte/incidencias']],
      ],
    ],
    ['Impresión', [['Impresión', ['dte/reimpresion', 'dte/formato']]]],
  ],
  gerencia: [['Panel', [['Indicadores', ['gerencia/panel']]]]],
  informes: [
    [
      'Comerciales',
      [['Ventas y clientes', ['informes/ventas', 'informes/clientes', 'informes/cobranza']]],
    ],
    [
      'Abastecimiento',
      [
        [
          'Stock y compras',
          ['informes/stock', 'informes/compras', 'informes/proveedores', 'informes/pagos'],
        ],
      ],
    ],
    [
      'Gestión',
      [
        ['Operación', ['informes/operaciones', 'informes/dte']],
        ['Resultados', ['informes/rentabilidad']],
      ],
    ],
  ],
  configuracion: [
    [
      'Productos',
      [
        [
          'Productos',
          ['configuracion/productos', 'configuracion/presentaciones', 'configuracion/codigos'],
        ],
        ['Clasificación', ['configuracion/categorias', 'configuracion/marcas']],
      ],
    ],
    [
      'Comercial',
      [
        [
          'Precios',
          ['configuracion/listas', 'configuracion/tratamiento-listas', 'configuracion/escalas'],
        ],
        [
          'Venta y despacho',
          ['configuracion/vendedores', 'configuracion/zonas', 'configuracion/costo-logistico'],
        ],
        ['Reparto', ['configuracion/camiones', 'configuracion/choferes', 'configuracion/rutas']],
        ['Condiciones e impuestos', ['configuracion/condiciones', 'configuracion/impuestos']],
      ],
    ],
    [
      'Organización',
      [
        [
          'Empresa',
          ['configuracion/empresas', 'configuracion/bodegas', 'configuracion/parametros'],
        ],
        [
          'Usuarios y permisos',
          [
            'configuracion/usuarios',
            'configuracion/roles',
            'configuracion/permisos',
            'configuracion/aprobaciones',
          ],
        ],
      ],
    ],
    [
      'Sistema',
      [
        ['Integración', ['configuracion/integraciones', 'configuracion/api-keys']],
        ['Control', ['configuracion/auditoria', 'configuracion/mapa']],
      ],
    ],
  ],
};

/** Siglas de documento que se ven delante del nombre, como en Random. */
const CODIGOS: Record<string, string> = {
  'ventas/cotizacion': 'COV',
  'ventas/nota-nueva': 'NVV',
  'ventas/factura-directa': 'FCV',
  'ventas/boleta-directa': 'BLV',
  'ventas/guia-directa': 'GDV',
  'ventas/guia': 'GDV',
  'ventas/documentos': 'FCV·BLV',
  'ventas/nota-credito': 'NCV',
  'ventas/nc-nd': 'NCV·FDV',
  'compras/ordenes': 'OCC',
  'compras/oc-nueva': 'OCC',
  'compras/recepciones': 'GRC',
  'compras/recepcion-sin-oc': 'GRC',
  'compras/facturas': 'FCC',
  'compras/factura-nueva': 'FCC',
  'compras/ingreso-sin-factura': 'ISF',
  'compras/recibidos': 'XML',
  'compras/registro-compras': 'RCV',
  'compras/notas': 'NCC·NDC',
  'compras/devoluciones': 'DEV',
  'stock/transferencias': 'GTI',
  'stock/ajustes': 'GRI·GDI',
  'operaciones/od-nueva': 'ODD',
};

/** Reparte las funciones de un módulo en sus menús y grupos; las que sobran quedan solo para Ctrl+K. */
function organizar(m: Modulo): Modulo {
  const todas = m.secciones
    .flatMap((s) => s.pantallas)
    .map((p) => (CODIGOS[p.ruta] ? { ...p, codigo: CODIGOS[p.ruta]! } : p));
  const ubicadas = new Set<string>();
  const secciones: Seccion[] = (DISPOSICION[m.id] ?? []).flatMap(([menu, grupos]) =>
    grupos.map(([nombre, rutas]) => ({
      menu,
      nombre,
      pantallas: rutas.flatMap((r) => {
        const p = todas.find((x) => x.ruta === r);
        if (!p) return [];
        ubicadas.add(r);
        return [p];
      }),
    })),
  );
  const resto = todas.filter((p) => !ubicadas.has(p.ruta));
  return { ...m, secciones: resto.length ? [...secciones, { pantallas: resto }] : secciones };
}

export const MODULOS: Modulo[] = BASE.map(organizar);

/** Los menús de texto de un subsistema, en orden. */
export const menusDe = (m: Modulo) => [
  ...new Set(m.secciones.flatMap((s) => (s.menu ? [s.menu] : []))),
];

type PantallaUbicada = Pantalla & { modulo: Modulo; seccion?: string };

/** Todas las funciones, en orden de menú. */
export const PANTALLAS: PantallaUbicada[] = MODULOS.flatMap((m) =>
  m.secciones.flatMap((s) =>
    s.pantallas.map((p) => ({ ...p, modulo: m, ...(s.nombre ? { seccion: s.nombre } : {}) })),
  ),
);

export const pantallaPorRuta = (ruta: string): PantallaUbicada | null =>
  PANTALLAS.find((p) => p.ruta === ruta) ?? null;

export const NOMBRE_ESTADO: Record<EstadoBackend, string> = {
  A: 'Backend operativo',
  B: 'Backend parcial',
  C: 'Front preparado · backend pendiente',
};

/** Minúsculas y sin tildes: «Configuración» se encuentra escribiendo «configuracion». */
export const normalizar = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Comandos de Ctrl+K: funciones que calzan con TODAS las palabras escritas. «abrir» e «ir» calzan con
 * cualquiera, así «abrir stock» encuentra Consulta de stock. Primero las operaciones.
 */
export function buscarComandos(
  texto: string,
  visible: (p: PantallaUbicada) => boolean,
  limite = 6,
) {
  const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return [];
  return PANTALLAS.filter((p) => {
    if (!visible(p)) return false;
    const pajar = normalizar(
      `abrir ir ${p.nombre} ${p.modulo.nombre} ${p.modulo.corto} ${p.seccion ?? ''} ${(p.alias ?? []).join(' ')}`,
    );
    return palabras.every((w) => pajar.includes(w));
  })
    .sort((a, b) => Number(!!b.accion) - Number(!!a.accion))
    .slice(0, limite);
}
